import { useEffect } from 'react';
import { X, Radar, Route, Network, ShieldQuestion } from 'lucide-react';

/* How the map is built, as four panels with a diagram each.
 *
 * This used to be a paragraph across the bottom of the map. It is good copy and
 * technical readers do want it, but it competed with the board for attention
 * every second the page was open. Behind a button it can be longer and better
 * drawn instead of shorter and ignored.
 *
 * The diagrams animate on a loop because each one describes a thing that moves:
 * a sweep going out, a packet crossing a router, two probes getting different
 * answers. A static picture of a sweep is just dots.
 */

function Cmd({ children }) {
  return (
    <code
      className="px-1.5 py-0.5 rounded bg-slate-900 text-slate-100 text-[11px]"
      style={{ fontFamily: 'JetBrains Mono, monospace' }}
    >
      {children}
    </code>
  );
}

// Panel 1: a beacon ARP-sweeping its own broadcast domain.
function ArpDiagram() {
  const hosts = [[26, 22], [70, 16], [114, 24], [150, 46], [26, 68], [70, 76], [114, 68]];
  return (
    <svg viewBox="0 0 176 96" className="w-full h-[96px]" aria-hidden="true">
      <circle cx="88" cy="46" r="10" className="fill-shinobi-purple" />
      <circle cx="88" cy="46" r="10" fill="none" stroke="#7c4ad0" strokeWidth="1.5" className="map-sweep" />
      <circle cx="88" cy="46" r="10" fill="none" stroke="#7c4ad0" strokeWidth="1.5" className="map-sweep map-sweep-2" />
      {hosts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="4.5" fill="#00ce3e" className="map-blip" style={{ animationDelay: `${i * 0.18}s` }} />
      ))}
      <text x="88" y="70" textAnchor="middle" className="fill-slate-500" style={{ fontSize: 8 }}>beacon</text>
    </svg>
  );
}

// Panel 2: a routed subnet, swept over the gateway, so no hardware addresses.
function RoutedDiagram() {
  return (
    <svg viewBox="0 0 176 96" className="w-full h-[96px]" aria-hidden="true">
      <circle cx="24" cy="48" r="9" className="fill-shinobi-purple" />
      <rect x="76" y="36" width="24" height="24" rx="5" fill="#334155" />
      <text x="88" y="74" textAnchor="middle" className="fill-slate-500" style={{ fontSize: 8 }}>router</text>
      {[0, 1, 2].map((i) => (
        <circle key={i} cx={148} cy={26 + i * 22} r="4.5" fill="#94a3b8" />
      ))}
      <line x1="33" y1="48" x2="76" y2="48" stroke="#cbd5e1" strokeWidth="1.6" />
      <line x1="100" y1="48" x2="140" y2="26" stroke="#cbd5e1" strokeWidth="1.2" />
      <line x1="100" y1="48" x2="140" y2="48" stroke="#cbd5e1" strokeWidth="1.2" />
      <line x1="100" y1="48" x2="140" y2="70" stroke="#cbd5e1" strokeWidth="1.2" />
      <circle r="3" fill="#7c4ad0" className="map-packet">
        <animateMotion dur="2.2s" repeatCount="indefinite" path="M 33 48 L 76 48 L 100 48 L 140 26" />
      </circle>
    </svg>
  );
}

// Panel 3: one hardware address seen in two subnets is a host bridging them.
function BridgeDiagram() {
  return (
    <svg viewBox="0 0 176 96" className="w-full h-[96px]" aria-hidden="true">
      <rect x="6" y="18" width="62" height="60" rx="8" fill="#fff" stroke="#e2e8f0" />
      <rect x="108" y="18" width="62" height="60" rx="8" fill="#fff" stroke="#e2e8f0" />
      <text x="37" y="14" textAnchor="middle" className="fill-slate-400" style={{ fontSize: 7.5, fontFamily: 'JetBrains Mono, monospace' }}>10.0.1.0/24</text>
      <text x="139" y="14" textAnchor="middle" className="fill-slate-400" style={{ fontSize: 7.5, fontFamily: 'JetBrains Mono, monospace' }}>10.0.9.0/24</text>
      <circle cx="30" cy="40" r="4" fill="#00ce3e" />
      <circle cx="48" cy="62" r="4" fill="#00ce3e" />
      <circle cx="152" cy="40" r="4" fill="#00ce3e" />
      <circle cx="130" cy="62" r="4" fill="#00ce3e" />
      <line x1="52" y1="48" x2="124" y2="48" stroke="#7c4ad0" strokeWidth="2" strokeDasharray="4 3" className="map-bridge" />
      <circle cx="52" cy="48" r="7" fill="#7c4ad0" />
      <circle cx="124" cy="48" r="7" fill="#7c4ad0" />
      <text x="88" y="90" textAnchor="middle" className="fill-slate-500" style={{ fontSize: 8 }}>same hardware address</text>
    </svg>
  );
}

// Panel 4: two beacons, one host, two different answers.
function ProbeDiagram() {
  return (
    <svg viewBox="0 0 176 96" className="w-full h-[96px]" aria-hidden="true">
      <circle cx="20" cy="24" r="8" className="fill-shinobi-purple" />
      <circle cx="20" cy="72" r="8" className="fill-shinobi-purple" />
      <circle cx="146" cy="48" r="12" fill="#ef4444" />
      <line x1="30" y1="26" x2="132" y2="44" stroke="#00ce3e" strokeWidth="2" strokeLinecap="round" />
      <line x1="30" y1="70" x2="132" y2="52" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 4" />
      <circle r="3" fill="#00ce3e" className="map-packet">
        <animateMotion dur="1.6s" repeatCount="indefinite" path="M 30 26 L 132 44" />
      </circle>
      <text x="74" y="20" textAnchor="middle" className="fill-shinobi-green" style={{ fontSize: 8, fontWeight: 700 }}>open</text>
      <text x="74" y="86" textAnchor="middle" className="fill-slate-400" style={{ fontSize: 8, fontWeight: 700 }}>filtered</text>
    </svg>
  );
}

const PANELS = [
  {
    icon: Radar,
    title: 'Hosts are found by sweeping',
    diagram: ArpDiagram,
    body: (
      <>
        Every subnet a beacon sits on is ARP swept, which is how a machine finds its own
        neighbours. That gives both the address and the hardware address of everything alive.
      </>
    ),
  },
  {
    icon: Route,
    title: 'Subnets behind a router are swept over it',
    diagram: RoutedDiagram,
    body: (
      <>
        ARP does not cross a router, so a subnet on the other side is swept with <Cmd>nmap -sn</Cmd>{' '}
        instead. That finds the hosts but not their hardware addresses, because the replies carry
        the router&apos;s.
      </>
    ),
  },
  {
    icon: Network,
    title: 'Routing comes from the beacon itself',
    diagram: BridgeDiagram,
    body: (
      <>
        Each subnet gets its gateway from the beacon&apos;s own routing table. A hardware address
        appearing in two subnets marks a host that bridges them, which is a path between segments
        that no firewall sees.
      </>
    ),
  },
  {
    icon: ShieldQuestion,
    title: 'Reachability is tested, not observed',
    diagram: ProbeDiagram,
    body: (
      <>
        Every beacon runs a TCP connect scan <Cmd>nmap -Pn -n -sT</Cmd> against the other hosts in
        its vault, and each result is stored against the beacon that produced it. Two beacons
        disagreeing about the same host means something filters by source address.
      </>
    ),
  },
];

export default function MapMethodModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] map-fade-in" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="How this map is built"
        className="fixed inset-0 z-[61] flex items-start justify-center p-4 sm:p-8 overflow-y-auto pointer-events-none"
      >
        <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden pointer-events-auto map-modal-in">
          <div className="flex items-start gap-3 px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-shinobi-purple/8 to-transparent">
            <div>
              <h2 className="text-base font-bold text-slate-900">How this map is built</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Shinobi draws only what its beacons can see and test. Here is exactly what that means.
              </p>
            </div>
            <button
              onClick={onClose}
              className="ml-auto text-slate-400 hover:text-slate-900 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-px bg-slate-200">
            {PANELS.map((p, i) => {
              const Icon = p.icon;
              const Diagram = p.diagram;
              return (
                <div
                  key={p.title}
                  className="bg-white p-5 map-panel-in"
                  style={{ animationDelay: `${80 + i * 90}ms` }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-7 h-7 rounded-lg bg-shinobi-purple/10 border border-shinobi-purple/25 flex items-center justify-center text-shinobi-purple shrink-0">
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <h3 className="text-[13px] font-bold text-slate-900">{p.title}</h3>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-100 mb-3 py-2">
                    <Diagram />
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>

          <div className="px-6 py-4 border-t border-slate-200 bg-amber-50/50 flex gap-2.5 items-start">
            <ShieldQuestion className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-900 leading-relaxed">
              <b>Nothing here captures traffic.</b> Everything above is an active test run from a
              beacon, so a path no beacon has probed is shown as unmeasured rather than closed. An
              empty picture means nobody looked, and the map says so rather than letting you read it
              as clean.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
